import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { Cable, ArrowUpRight } from 'lucide-react';
import { useAuth } from 'context/auth';
import Login from 'pages/Login';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { mcpRequest } from './McpAPI';
import { ReadAccess } from './McpSetup';
import './mcp.css';

export default function McpAuthorize() {
    const { auth } = useAuth();
    const location = useLocation();
    const request = new URLSearchParams(location.search).get('request');
    if (!auth) return <Login />;
    if (!auth.admin)
        return (
            <main className="mcp-page mcp-consent">
                <p>Only an internal Frontdoor administrator can authorize this connection.</p>
            </main>
        );
    if (!/^[a-f0-9]{64}$/.test(request || ''))
        return (
            <main className="mcp-page mcp-consent">
                <p>Invalid connection request. Start again from your AI app.</p>
            </main>
        );
    return <Consent key={request} request={request} />;
}

function Consent({ request }) {
    const attempt = useRef(0);
    useEffect(
        () => () => {
            attempt.current += 1;
        },
        [],
    );
    const [label, setLabel] = useState('');
    const [pending, setPending] = useState(false);
    const [error, setError] = useState('');
    const query = useQuery({
        queryKey: ['admin', 'mcp-consent', request],
        queryFn: () => mcpRequest(`/authorization/${request}`),
        retry: false,
    });
    async function decide(approved) {
        const currentAttempt = ++attempt.current;
        setPending(true);
        setError('');
        try {
            const { redirectUrl } = await mcpRequest(`/authorization/${request}`, 'POST', {
                approved,
                label: label.trim() || query.data.clientName,
            });
            if (currentAttempt !== attempt.current) return;
            window.location.assign(redirectUrl);
        } catch (err) {
            if (currentAttempt !== attempt.current) return;
            setError(err.message);
            setPending(false);
        }
    }
    return (
        <main className="mcp-page mcp-consent">
            <section className="mcp-panel mcp-consent-card">
                <div className="mcp-empty-icon">
                    <Cable size={30} />
                </div>
                <span className="mcp-eyebrow">FRONTDOOR / CONNECT AN APP</span>
                <h1>Give your AI read access.</h1>
                {query.isPending && <p role="status">Loading the connection request…</p>}
                {(query.isError || error) && (
                    <p className="mcp-form-error" role="alert">
                        {error || query.error.message}
                    </p>
                )}
                {query.data && (
                    <>
                        <p>
                            <strong>{query.data.clientName}</strong> is requesting access to
                            internal Frontdoor data.
                        </p>
                        <div className="mcp-form-notice">
                            This includes personal customer data and orders across every
                            organization. The app name is provided by the requesting client; verify
                            the callback below.
                        </div>
                        <ReadAccess />
                        <div className="mcp-callback">
                            <span>Return to</span>
                            <code>{query.data.redirectUri}</code>
                        </div>
                        <form
                            className="mcp-form"
                            onSubmit={(event) => {
                                event.preventDefault();
                                decide(true);
                            }}
                        >
                            <label htmlFor="mcp-consent-name">Name this connection</label>
                            <Input
                                id="mcp-consent-name"
                                maxLength={80}
                                value={label}
                                placeholder={query.data.clientName}
                                onChange={(event) => setLabel(event.target.value)}
                            />
                            <p className="mcp-fine-print">
                                Read only · Expires after 90 days · Revoke anytime in AI connections
                            </p>
                            <div className="mcp-dialog-actions">
                                <Button
                                    type="button"
                                    variant="outline"
                                    disabled={pending}
                                    onClick={() => decide(false)}
                                >
                                    Deny
                                </Button>
                                <Button type="submit" disabled={pending}>
                                    {pending ? 'Connecting…' : 'Allow read access'}
                                    <ArrowUpRight />
                                </Button>
                            </div>
                        </form>
                    </>
                )}
            </section>
        </main>
    );
}
